import React, {useEffect, useRef, useState} from "react";
import {IItemContent} from "@connox/typescript-library";
import * as Sentry from "@sentry/browser";
import styles from "./Image.module.scss";

// 1x1 white pixel image data
export const blankImage = "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7";

// language code, including fix for .dk, and .nl (no placeholder available)
let lang = document.body.dataset.lang || "en";
lang = lang === "da" ? "dk" : lang;
lang = lang === "nl" ? "en" : lang;

// 'No image available' placeholder png url
const placeholder = `/images/placeholder-small-${lang}.png`;

const Image: React.FC<{item: IItemContent}> = ({item}) => {
  const {images, title, url} = item;
  const [loadedClass, setLoadedClass] = useState<string | undefined>(styles.image);
  const imageRef = useRef<HTMLImageElement>(null);

  /**
   *  first off set the local blank image pixel
   *  to reserve space and to not show the alt text
   *  this lets the next useEffect set the actual image
   *  then 'onload' triggers when pic is loaded over network
   *  and handler then fades the img in
   */
  useEffect(() => {
    if (!imageRef.current) {
      return;
    }
    imageRef.current.src = blankImage;
    imageRef.current.srcset = "";
    setLoadedClass(styles.image);
  }, [images]);

  /**
   * retrieve sources of images, including some debugging
   */
  useEffect(() => {
    // Only proceed after 1x1 pixel is shown in the real dom
    if (!imageRef.current || imageRef.current.src !== blankImage) {
      return;
    }

    const squareFree = images.find((image) => image.mediaTypeId === 100016);
    // Should never be the case
    if (!squareFree?.sizes?.length) {
      Sentry.addBreadcrumb({
        category: "no.media",
        level: "warning",
        message: "no media found for item",
        data: {
          url,
          images,
        },
      });

      return;
    }

    const image1x = squareFree.sizes.find((size) => size.name === "half-2x")?.url || placeholder;
    const image2x = squareFree.sizes.find((size) => size.name === "full-2x")?.url;

    imageRef.current.src = image1x;
    if (image2x) {
      imageRef.current.srcset = `${image1x} 1x, ${image2x} 2x`;
    }
  }, [imageRef.current?.src]);

  /**
   * On load fade picture in
   */
  const onLoad = () => {
    setLoadedClass(`${styles.image} ${styles.loaded}`);
  };

  /**
   * On error set placeholder and debugging information
   */
  const onError = () => {
    if (!imageRef.current) return;

    imageRef.current.dataset.brokenSrc = imageRef.current.src;
    imageRef.current.dataset.brokenSrcSet = imageRef.current.srcset;

    imageRef.current.src = placeholder;
    imageRef.current.srcset = "";
  };

  return <img ref={imageRef} alt={title} onLoad={onLoad} onError={onError} className={loadedClass} />;
};

export default Image;
